import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaLock, FaSave, FaUser, FaIdBadge, FaUserTag } from 'react-icons/fa';

const fieldClass =
    'block w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-800 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100';

const labelClass =
    'mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400';

const Settings = ({ data }) => {
    const navigate = useNavigate();
    const header = {
        headers: { Authorization: `Bearer ${data.token}` },
        withCredentials: true,
    };

    const [nama, setNama] = useState(data?.user?.nama || '');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setNama(data?.user?.nama || '');
    }, [data?.user?.nama]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!nama.trim()) {
            Swal.fire('Gagal', 'Nama wajib diisi.', 'error');
            return;
        }

        const changingPassword = !!(newPassword || confirmPassword || currentPassword);
        if (changingPassword) {
            if (!currentPassword) {
                Swal.fire('Gagal', 'Password lama wajib diisi.', 'error');
                return;
            }
            if (!newPassword || newPassword.length < 6) {
                Swal.fire('Gagal', 'Password baru minimal 6 karakter.', 'error');
                return;
            }
            if (newPassword !== confirmPassword) {
                Swal.fire('Gagal', 'Konfirmasi password tidak cocok.', 'error');
                return;
            }
        }

        const payload = { nama: nama.trim() };
        if (changingPassword) {
            payload.currentPassword = currentPassword;
            payload.newPassword = newPassword;
        }

        setSaving(true);
        try {
            await axios.patch('api/auth/profile', payload, header);

            if (typeof data?.refreshAuth === 'function') {
                data.refreshAuth();
            } else {
                window.dispatchEvent(new Event('auth:refresh'));
            }

            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');

            Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Pengaturan akun berhasil disimpan.',
                timer: 1400,
                showConfirmButton: false,
            });
        } catch (err) {
            Swal.fire(
                'Gagal',
                err?.response?.data?.message || 'Tidak bisa menyimpan pengaturan.',
                'error'
            );
        } finally {
            setSaving(false);
        }
    };

    const role = data?.user?.role || '-';
    const username = data?.user?.username || '-';

    return (
        <>
            <form onSubmit={handleSubmit} className="space-y-4 pb-8">
                <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
                    <aside className="h-fit overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                        <div className="bg-gradient-to-br from-primary to-primary-focus px-5 py-6 text-white">
                            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 text-xl">
                                <FaIdBadge />
                            </div>
                            <p className="text-xs font-semibold uppercase tracking-wider text-white/70">
                                Akun saya
                            </p>
                            <h2 className="mt-1 truncate text-lg font-bold">{nama || '—'}</h2>
                            <p className="mt-0.5 truncate text-sm text-white/75">@{username}</p>
                        </div>
                        <div className="space-y-3 px-5 py-4 text-sm">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-gray-500 dark:text-gray-400">Role</span>
                                <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold capitalize text-primary dark:text-primary-on-dark">
                                    {role}
                                </span>
                            </div>
                            <p className="rounded-xl bg-gray-50 px-3 py-2.5 text-xs text-gray-600 dark:bg-gray-900/40 dark:text-gray-300">
                                Ubah nama tampilan dan password akun Anda.
                            </p>
                        </div>
                    </aside>

                    <div className="space-y-4">
                        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                            <h3 className="mb-4 flex items-center gap-2 text-sm font-bold text-gray-800 dark:text-gray-100">
                                <FaUser className="text-primary" />
                                Profil
                            </h3>
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <label className={labelClass}>
                                        <FaUserTag className="opacity-70" />
                                        Username
                                    </label>
                                    <input
                                        type="text"
                                        value={username}
                                        disabled
                                        className={`${fieldClass} disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-900/50`}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>
                                        Nama <span className="text-red-400">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={nama}
                                        onChange={(e) => setNama(e.target.value)}
                                        placeholder="Nama lengkap"
                                        className={fieldClass}
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                            <h3 className="mb-1 flex items-center gap-2 text-sm font-bold text-gray-800 dark:text-gray-100">
                                <FaLock className="text-primary" />
                                Ubah Password
                            </h3>
                            <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                                Kosongkan jika tidak ingin mengganti password.
                            </p>
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="sm:col-span-2">
                                    <label className={labelClass}>Password lama</label>
                                    <input
                                        type="password"
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className={fieldClass}
                                        autoComplete="current-password"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Password baru</label>
                                    <input
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Minimal 6 karakter"
                                        className={fieldClass}
                                        autoComplete="new-password"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Konfirmasi password baru</label>
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Ulangi password baru"
                                        className={fieldClass}
                                        autoComplete="new-password"
                                    />
                                </div>
                            </div>
                        </section>

                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button
                                type="button"
                                onClick={() => navigate('/admin/dashboard')}
                                className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-bold text-gray-600 transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                                Batal
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="inline-flex items-center justify-center gap-2 rounded-xl btn-primary disabled:opacity-50"
                            >
                                <FaSave />
                                {saving ? 'Menyimpan...' : 'Simpan'}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </>
    );
};

export default Settings;
